/**
 * PLAT-9968 / PLAT-10046 — Composition quick-edit popover.
 *
 * Opens when the user clicks a CardChip (intro/end) or NametagChip in the
 * clip-preview transcript, or via the canvas action menu. Two display modes
 * depending on the active caption language:
 *
 *   • **Original language** (PLAT-9968) — one editable textarea per
 *     `allowQuickEdit: true` text control, prefilled with the original-
 *     language value. Save writes back through
 *     `editor.commandUpdateElement`.
 *
 *   • **Non-original language** (PLAT-10046) — translatable rows render as
 *     read-only pairs: original (top, disabled) + translated (bottom,
 *     read-only) with a `Phrase` / `Preset` / `Missing` resolution badge.
 *     Non-translatable quick-edit rows still render as editable original-
 *     language inputs. Two footer buttons appear depending on coverage:
 *     "Translate composition" when any row is `Missing`, "Retranslate
 *     composition" (with `force: true`) when every row resolves and at
 *     least one resolved via `phrase`. Preset-only compositions hide both
 *     buttons since runtime regenerate can't unseat preset translations.
 *
 * Translated-value editing + per-row pin write + per-row retranslate +
 * "Restore preset" are deferred to a follow-up sibling ticket.
 */
import {
  type CompositionQuickEditSurface,
  type GraphicsAITextIdentity,
  trackCompositionPhraseEdited,
  trackCompositionRetranslateClicked,
  trackCompositionTranslateClicked,
  trackCompositionTranslateFailed,
} from '@src/analytics/EventTracker';
import { MODAL_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import { CLIP_PREVIEW_Z } from '@src/components/content-components/clipPreviewZ';
import { AITextControl } from '@src/components/sidepanel/CompositionSettings/controls/AITextControl';
import { IDLE_AI_TEXT_STATUS } from '@src/components/sidepanel/CompositionSettings/types';
import ChevronDownIcon from '@src/svgs/ChevronDownIcon';
import CloseIcon from '@src/svgs/CloseIcon';
import { isOriginalLang } from '@src/utility/captionLanguage';
import { extractErrorInfo } from '@src/utility/error';
import { formatPinnedAt } from '@src/utility/formatPinnedAt';
import { formatLanguageCaption } from '@src/utility/languageCaption';
import { LogManager } from '@src/utility/Logger';
import {
  DEFAULT_FRAMERATE,
  formatTimecode,
  parseTimecode,
  wholeFps,
} from '@src/utility/timecode';
import { normalizePhrase } from '@src/videoTranscriptionProcessor/translation/normalizePhrase';
import { resolveCompositionFieldTextWithSource } from '@src/videoTranscriptionProcessor/translation/resolveCompositionFieldText';
import { setCompositionFieldValue } from '@src/videoTranscriptionProcessor/translation/setCompositionFieldValue';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Tooltip } from 'react-tooltip';
import styled, { keyframes } from 'styled-components';

import { computeQuickEditPlacement } from './compositionQuickEditPlacement';
import {
  ButtonRow,
  DisclosureChevron,
  fieldFocusCss,
  OriginalReference,
  PrimaryButton,
  SecondaryButton,
  Spacer,
} from './translationEditorPrimitives';
import { useExclusiveTranslationPopover } from './useExclusiveTranslationPopover';

import type {
  ClearRect,
  PopoverPlacement,
} from './compositionQuickEditPlacement';
import type { AITextTranscriptElement } from '@src/components/sidepanel/CompositionSettings/controls/useAITextBaseText';
import type { AITextBaseTextScope } from '@src/components/sidepanel/CompositionSettings/types';
import type { TimelineMarker } from '@src/components/transcript/hooks/useTimelineMarkers';
import type { EditorStoreLike } from '@src/utility/compositionSettings';
import type {
  CompositionPhraseTranslation,
  CompositionTranslationOutcome,
} from '@src/videoTranscriptionProcessor/translation/compositionTypes';
import type { CompositionQuickEditField } from '@src/videoTranscriptionProcessor/translation/extractCompositionQuickEditFields';
import type { ResolvedSource } from '@src/videoTranscriptionProcessor/translation/resolveCompositionFieldText';

const logger = LogManager.getLogger('component:CompositionQuickEditPopover');

/**
 * PLAT-10913 — Generate stays inert on a surface that passes no
 * `aiTextContext.generate`. Module-level so the identity is stable across
 * renders.
 */
const noopGenerate = () => {};

// Timecode parse/format moved to `@src/utility/timecode` (PLAT-10951) so the
// clip-comment timecode field shares one validating parser with this popover.
// Re-exported here because both are part of this module's public surface.
export { formatTimecode, parseTimecode };

/**
 * PLAT-10046 — callback shape passed by the surface (bridge / canvas
 * layer) to dispatch a per-composition retranslate. The surface holds the
 * live `VideoSourceStore` + `extraElementData` needed to build
 * `compositionTargets`, plus the active language and the
 * `TranslationStore.retranslateCompositionPhrases` reference — it wraps
 * those into this minimal closure so the popover stays surface-agnostic.
 */
export type RetranslateCompositionDispatch = (args: {
  force: boolean;
  compositionElementId?: string;
  sourceTexts?: string[];
}) => Promise<{ compositions?: CompositionTranslationOutcome }>;

/**
 * PLAT-10046 — callback shape for committing a single manual translated-
 * value pin. The surface wraps
 * `TranslationStore.upsertCompositionPhraseTranslation` and stamps the
 * per-placement `references[]` so a new phrase entry (no prior bulk run)
 * still carries reference data for future bulk runs.
 */
export type UpsertCompositionPhraseDispatch = (args: {
  sourceText: string;
  translatedValue: string;
  /**
   * PLAT-10046 — the translated value the user saw immediately before
   * this override. Set for preset rows (where no phrase entry exists yet
   * and the preset value would otherwise be lost on first pin); left
   * undefined for `'phrase'` rows because the cached entry's own
   * `translatedValue` is already the right baseline. The store
   * snapshots this into `originalTranslatedValue` so revert always has
   * somewhere to land.
   */
  previousTranslatedValue?: string;
}) => void;

/**
 * PLAT-10046 — callback shape for reverting a manual translated-value
 * pin. The surface wraps `TranslationStore.removeCompositionPhrasePin`
 * which removes the pinned entry; projection then falls back to
 * preset / original. The user can hit Retranslate to repopulate AI.
 */
export type RemoveCompositionPhrasePinDispatch = (args: {
  sourceText: string;
}) => void;

export interface CompositionQuickEditPopoverProps {
  /** Composition placement id OR top-level overlay element id. */
  targetId: string;
  /** Heading text shown at the top of the popover. */
  headerLabel: string;
  anchorElement: HTMLElement;
  /**
   * PLAT-11090 — the selected graphic's projected viewport box, so the popover
   * opens BELOW the graphic instead of below the anchor. The canvas anchor is
   * the pencil in the action pill, which the layer parks inside that box, so
   * anchoring to it covers the very thing being edited. Omit on the transcript
   * surface, where the anchor is already clear. See `computeQuickEditPlacement`.
   */
  clearRect?: ClearRect | null;
  fields: CompositionQuickEditField[];
  /**
   * Editor store for the surface this popover is rendered on. Original-
   * text edits flow through `setCompositionFieldValue` →
   * `modifyCompositionProperty` → `editor.commandUpdateElement`, the
   * same path Creator Studio's side-panel text controls use. The
   * popover types this as `EditorStoreLike` so it stays trivially
   * mountable on any surface that exposes the standard editor.
   */
  editorStore: EditorStoreLike;
  /**
   * PLAT-10046 — active caption language read at mount time. Drives the
   * non-original render path: read-only translated rows + footer
   * translate/retranslate buttons. `'original'` (or any value where
   * `isOriginalLang` is true) renders the PLAT-9968 single-input shape.
   */
  activeLanguage: string;
  /**
   * PLAT-10046 — per-language composition-phrase lookup from
   * `TranslationStore.getCompositionPhrasesByNormalizedSource`. Used to
   * (a) resolve the current translated value per row and (b) compute the
   * shared-phrase footprint for the retranslate confirm step. Empty Map
   * is acceptable (the popover renders every translatable row as
   * `Missing` and surfaces "Translate composition").
   */
  compositionPhrases: ReadonlyMap<string, CompositionPhraseTranslation>;
  /**
   * PLAT-10046 — dispatch callback for the Translate / Retranslate
   * buttons. When omitted, the footer buttons stay hidden even under a
   * translated language (use this to render a read-only popover on
   * surfaces that don't wire the store yet). The surface (bridge /
   * canvas layer) wraps `TranslationStore.retranslateCompositionPhrases`
   * with the live `compositionTargets` + `lang` so the popover only
   * decides `force` / `sourceTexts` / `compositionElementId`.
   */
  retranslateCompositionPhrases?: RetranslateCompositionDispatch;
  /**
   * PLAT-10046 — dispatch a manual translated-value pin write
   * (`pinnedOverrideByManualEdit: true`) for one row. When omitted, the
   * translated row stays read-only (Save commits original-language edits
   * only). Wired by the surface (bridge / canvas layer) with the live
   * `references[]` for the placement so a brand-new phrase entry still
   * carries that data for future bulk runs.
   */
  upsertCompositionPhraseTranslation?: UpsertCompositionPhraseDispatch;
  /**
   * PLAT-10046 — dispatch to remove a manual phrase pin (the inverse of
   * upsert). Powers the "Revert" button inside the pin-detail card that
   * opens when the "Phrase Override" badge is clicked. When omitted, the
   * detail card hides the Revert button.
   */
  removeCompositionPhrasePin?: RemoveCompositionPhrasePinDispatch;
  /**
   * PLAT-10046 — source language code (e.g. `'en-US'`). Shown as a display
   * name in the header language pill ("American English → Italian (Italy)",
   * PLAT-10644) under translated mode. When omitted, the pill shows the
   * target language alone.
   */
  sourceLanguage?: string;
  /**
   * Active caption language display name (e.g. `'European Spanish'`).
   * Rendered top-right in the header next to the close button under a
   * translated language so the user knows which language pin they're
   * editing. `null` / omitted (original language) hides it.
   */
  activeLanguageLabel?: string | null;
  /**
   * Fires when the user saves at least one row. Caller is responsible
   * for emitting `composition_text_edited` events with whatever
   * per-row context it has (typically the popover doesn't see story
   * id / handler id, the caller does). `translatedRowsCommitted` counts
   * dirty translated rows pinned via `upsertCompositionPhraseTranslation`.
   */
  onSaved?: (summary: {
    originalRowsCommitted: number;
    translatedRowsCommitted: number;
  }) => void;
  /**
   * PLAT-10046 analytics context. When all three are supplied AND the
   * popover dispatches a translate / retranslate, it fires the matching
   * BEHAVIORAL events. Optional so the popover stays renderable on test
   * harnesses + future surfaces that haven't wired analytics yet.
   */
  storyId?: string | null;
  compositionType?: 'introCard' | 'endCard' | 'nameCard' | 'overlay' | null;
  surface?: CompositionQuickEditSurface;
  /**
   * Current overlay timing (seconds) for the start/end timecode fields.
   * When supplied together with `onCommitTiming` AND `compositionType` is
   * `'overlay'`, the popover renders editable start/end inputs above the
   * text rows (so they show even for plain overlays with no editable text).
   * Omitted for cards / nametags — their timing is placement-managed.
   */
  timing?: { time: number; endTime: number };
  /**
   * Commit new overlay timing. Routes to the same
   * `editor.commandUpdateElement(id, { time, duration })` path the drag
   * handles use. Omit to keep the timing fields hidden.
   */
  onCommitTiming?: (next: { time: number; duration: number }) => void;
  /**
   * Frame rate for the start/end timecodes + frame-by-frame steppers.
   * Defaults to 30 when a surface doesn't supply one.
   */
  framerate?: number;
  /**
   * PLAT-10913 — what an `aiText` row needs to resolve its base text: the
   * clip's transcript elements plus the marker for this composition (the
   * anchor / time window `findOverlayRanges` maps from). Omit on surfaces
   * that haven't wired it — `aiText` rows then render as plain editable text
   * rows, exactly as they did before this ticket.
   */
  aiTextContext?: {
    transcriptElements:
      | ReadonlyArray<AITextTranscriptElement>
      | null
      | undefined;
    overlay: TimelineMarker | null;
    /**
     * PLAT-10915 — run a generation for one `aiText` row. The surface owns the
     * repository call, the identifiers and the write-back; the popover only
     * decides which row asked and reflects the result in that row's input.
     * Resolves with the text written, or `null` when the generation failed —
     * the reason comes back through `aiTextStatus`. Omit and Generate stays
     * inert (the PLAT-10913 shell behaviour).
     */
    generate?: (request: {
      field: CompositionQuickEditField;
      baseTextScope: AITextBaseTextScope;
      instructions: string;
    }) => Promise<string | null>;
    /**
     * PLAT-10915 — in-flight / error state for a row's control, held by the
     * surface so a generation that failed at INSERT is still reported (with
     * its retry) the first time quick edit is opened.
     */
    status?: (controlId: string) => {
      isGenerating: boolean;
      error: string | null;
    };
    /**
     * PLAT-10916 — what the last generation left behind for a row's control:
     * every option and the index last applied. Read live (a function, not a
     * snapshot on `fields`) because the blob is mutated in place — a snapshot
     * taken at mount would leave the picker empty right after Generate
     * resolves. `undefined` when the control has never generated.
     */
    provenance?: (controlId: string) =>
      | {
          options: ReadonlyArray<string>;
          selectedOption: number;
          /** PLAT-10917 — the steer that run carried, if any. */
          instructions?: string;
          /** PLAT-10917 — the scope that run read from. */
          baseTextScope?: AITextBaseTextScope;
          /**
           * PLAT-10918 — hash of the base text that run read. The control
           * re-hashes the CURRENT base text against it to flag "Transcript
           * changed"; the popover only carries it through.
           */
          sourceFingerprint?: string;
        }
      | undefined;
    /**
     * PLAT-10916 — apply one of those options. Synchronous: the surface writes
     * it to the element locally and returns the text, or `null` when the write
     * didn't land. Omit and the picker stays hidden.
     */
    selectOption?: (request: {
      field: CompositionQuickEditField;
      optionIndex: number;
    }) => string | null;
    /**
     * PLAT-10923 — analytics identity for a row's control, for the two events
     * the control fires itself (the base-text switch and the staleness
     * indicator). The popover only passes it through; omit and those two events
     * don't fire, exactly as Generate stays inert without `generate`.
     */
    analytics?: (controlId: string) => GraphicsAITextIdentity;
  };
  onClose: () => void;
}

export interface RowState {
  field: CompositionQuickEditField;
  /**
   * Current input value for the original-language row. In translated mode
   * this is only edited when the row is non-translatable (quick-edit-only).
   */
  value: string;
  /**
   * PLAT-10046 — current input value for the translated row. Seeded from
   * the resolver on mount + on `fields` change; the user can override and
   * Save dispatches a manual phrase pin.
   */
  translatedValue: string;
  /**
   * PLAT-10046 — the resolved source at mount time. Used to compare
   * against `translatedValue` for dirty detection (a user-edited value
   * different from the resolved value gets pinned on Save). Kept as the
   * resolver-supplied source so badge rendering stays in sync without
   * recomputing on every keystroke.
   */
  resolvedSource: ResolvedSource | null;
  /** Resolver-computed translated value at mount time (display + dirty baseline). */
  resolvedValue: string;
  /**
   * PLAT-10046 — `pinnedOverrideByManualEdit` from the matching phrase
   * entry, plus optional pin metadata (`pinnedBy` / `pinnedAt`). Drives
   * the "Phrase Override" badge visibility + the pin-detail card.
   * `originalTranslatedValue` is the pre-override translated value
   * (captured by the store on the first pin); the detail card surfaces
   * it under "Original phrase" so the user sees what they overrode.
   */
  pin: {
    pinned: boolean;
    pinnedBy?: string;
    pinnedAt?: string;
    originalTranslatedValue?: string;
  };
  /**
   * PLAT-10046 — set when the user clicked Revert on the pin-detail card
   * for this row. Reverting populates the translated input with the AI
   * baseline but does NOT commit. On Save:
   *   - if `translatedValue` still equals `revertBaseline` → call
   *     `removeCompositionPhrasePin` (drops the pin in the store).
   *   - if the user typed something different → treat as a normal
   *     override; `upsertCompositionPhraseTranslation` runs.
   * On Cancel: discarded; the pin in the store is untouched.
   */
  revertPending: boolean;
  revertBaseline?: string;
}

export const CompositionQuickEditPopover: React.FC<
  CompositionQuickEditPopoverProps
> = ({
  targetId,
  headerLabel,
  anchorElement,
  clearRect,
  fields,
  editorStore,
  activeLanguage,
  compositionPhrases,
  retranslateCompositionPhrases,
  upsertCompositionPhraseTranslation,
  removeCompositionPhrasePin,
  sourceLanguage,
  activeLanguageLabel,
  storyId,
  compositionType,
  surface,
  timing,
  onCommitTiming,
  framerate = DEFAULT_FRAMERATE,
  aiTextContext,
  onSaved,
  onClose,
}) => {
  const firstInputRef = useRef<HTMLTextAreaElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  // Computed once per open. The canvas layer re-polls the graphic's box at
  // 10Hz to track animations; feeding that in would make the popover walk
  // around the screen mid-edit.
  const placement = useMemo(
    () => computeQuickEditPlacement(anchorElement, clearRect),
    [anchorElement, clearRect],
  );

  // PLAT-10644 — opening any other translation popover (canvas layer,
  // transcript bridge, phrase editor) dismisses this one, so two editors
  // never sit open side by side. Dismissal = Cancel (draft discarded).
  useExclusiveTranslationPopover(onClose);

  const isTranslated = !isOriginalLang(activeLanguage);

  const [rows, setRows] = useState<RowState[]>(() =>
    buildInitialRows(fields, isTranslated, activeLanguage, compositionPhrases),
  );

  // Re-seed when the inputs change (e.g. parent swaps which composition
  // is being edited without unmounting — currently the parent unmounts
  // on marker change so this guards against future direct-anchor swaps).
  // Also re-seeds after `compositionPhrases` flips (post-retranslate),
  // which is the moment new translations need to land in the visible
  // inputs.
  useEffect(() => {
    setRows(
      buildInitialRows(
        fields,
        isTranslated,
        activeLanguage,
        compositionPhrases,
      ),
    );
  }, [fields, isTranslated, activeLanguage, compositionPhrases]);

  // PLAT-10046 — which row indices have the "Original ▸ <langcode>"
  // disclosure expanded. Unused in original mode (the original textarea is
  // the primary input there).
  //
  // PLAT-10512 — under a foreign language, default EVERY row's original
  // disclosure OPEN so the creator can compare original vs translation
  // side-by-side without expanding each row by hand. Original language has
  // no original disclosure to open, so the set stays empty there.
  const [expandedOriginals, setExpandedOriginals] = useState<Set<number>>(() =>
    isTranslated ? new Set(fields.map((_, i) => i)) : new Set<number>(),
  );

  // PLAT-10513 — the lazy seed above only runs once at mount. The popover has
  // no outside-click dismissal (only Escape / Cancel / X / Save), so the
  // transcript language toggle can flip `isTranslated` while it stays
  // mounted; a target swap can likewise reuse this instance. Re-seed the
  // default-open set on those transitions ONLY — deliberately NOT on
  // `compositionPhrases` — so a post-retranslate cache swap doesn't yank a
  // manually-collapsed row back open.
  useEffect(() => {
    setExpandedOriginals(
      isTranslated ? new Set(fields.map((_, i) => i)) : new Set<number>(),
    );
  }, [isTranslated, targetId, fields.length]);

  // PLAT-10046 — which row index has its pin-detail card open (the
  // panel that surfaces sourceText / pinnedBy / pinnedAt / Revert).
  // `null` = no card open. Only one card is open at a time.
  const [pinDetailIdx, setPinDetailIdx] = useState<number | null>(null);

  // Overlay start/end timecode fields. Only for `'overlay'` placements with
  // a wired commit path — cards / nametags keep placement-managed timing.
  const showTiming =
    !!timing && !!onCommitTiming && compositionType === 'overlay';
  const [startInput, setStartInput] = useState(() =>
    timing ? formatTimecode(timing.time, framerate) : '',
  );
  const [endInput, setEndInput] = useState(() =>
    timing ? formatTimecode(timing.endTime, framerate) : '',
  );
  // The disclosure starts collapsed — the summary shows Duration only; the
  // user expands to reveal (and edit) Start / End.
  const [timingExpanded, setTimingExpanded] = useState(false);
  // Track whether the user actually edited the timing fields. The display is
  // rounded to the nearest frame, so a float-diff against the precise
  // committed value could falsely read "changed" and silently snap the
  // overlay on Save. Only commit when truly dirty.
  const [timingDirty, setTimingDirty] = useState(false);
  // Re-seed when the overlay's committed timing (or frame rate) changes
  // underneath us (e.g. a drag-handle commit while the popover is open).
  useEffect(() => {
    if (!timing) return;
    setStartInput(formatTimecode(timing.time, framerate));
    setEndInput(formatTimecode(timing.endTime, framerate));
    setTimingDirty(false);
  }, [timing?.time, timing?.endTime, framerate]);

  // Nudge a timing field frame-by-frame via the up/down steppers. Snaps to
  // whole frames and clamps at zero.
  const stepTiming = useCallback(
    (which: 'start' | 'end', deltaFrames: number) => {
      const fps = wholeFps(framerate);
      const current = which === 'start' ? startInput : endInput;
      const fallback =
        which === 'start' ? (timing?.time ?? 0) : (timing?.endTime ?? 0);
      const baseSec = parseTimecode(current, fps) ?? fallback;
      const nextFrames = Math.max(0, Math.round(baseSec * fps) + deltaFrames);
      const formatted = formatTimecode(nextFrames / fps, fps);
      if (which === 'start') setStartInput(formatted);
      else setEndInput(formatted);
      setTimingDirty(true);
    },
    [startInput, endInput, timing?.time, timing?.endTime, framerate],
  );

  // Live duration for the disclosure summary — recomputed every render from
  // the current Start / End inputs so it updates as the user edits / steps.
  const durationDisplay = useMemo(() => {
    if (!showTiming) return '';
    const start = parseTimecode(startInput, framerate);
    const end = parseTimecode(endInput, framerate);
    if (start === null || end === null || end <= start) return '—';
    return formatTimecode(end - start, framerate);
  }, [showTiming, startInput, endInput, framerate]);

  // Decide whether the single "Retranslate" footer button shows.
  //   • Not translated → hide (per user feedback: translated mode only)
  //   • No retranslate dispatch wired → hide
  //   • No translatable rows → hide
  //   • Every translatable row resolves via preset only → hide
  //     (PLAT-9963 preset lock supersedes runtime regenerate; nothing
  //     for the LLM to do).
  const showRetranslate = useMemo(() => {
    if (!isTranslated) return false;
    if (!retranslateCompositionPhrases) return false;
    const translatableRows = rows.filter(
      r => r.field.isTranslatable && r.resolvedSource !== null,
    );
    if (translatableRows.length === 0) return false;
    const allPreset = translatableRows.every(
      r => r.resolvedSource === 'preset',
    );
    return !allPreset;
  }, [isTranslated, retranslateCompositionPhrases, rows]);

  // PLAT-10190 — first-time Translate vs Retranslate. Any unresolved
  // translatable row ('missing': no story phrase, no preset) means the first
  // pass hasn't run → label "Translate" + dispatch force: false (fill the
  // missing rows without clobbering existing phrases/pins). Once every
  // translatable row resolves (≥1 via phrase), it's "Retranslate" + force:
  // true (replace AI translations, drop non-preset pins).
  const isFirstTimeTranslate = useMemo(
    () =>
      rows.some(r => r.field.isTranslatable && r.resolvedSource === 'missing'),
    [rows],
  );

  // Shared-phrase confirm step + in-flight + error state for the
  // retranslate button. `null` confirmStep means no confirm is pending.
  const [confirmStep, setConfirmStep] = useState<{
    otherCompositionsCount: number;
    affectedRefsCount: number;
  } | null>(null);
  const [inFlight, setInFlight] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Intentionally NOT using `useOutsideAlerter` here. The alerter
  // listens for parent-window blur and treats iframe-focus events as
  // outside clicks; in the clip-preview surface every click on the
  // Creatomate iframe (re-selecting a composition, scrubbing the
  // playhead) blurs the parent window and would dismiss this popover
  // before the user can type or click Save. Dismissal is explicit:
  // Cancel button, X button, Escape (handleKeyDown below), Save, or
  // selecting a different composition (parent clears popoverAnchor).

  useEffect(() => {
    // Focus the first input. Without an autofocus the popover requires
    // an extra tab to reach the editing affordance. Skip in translated
    // mode when every row is read-only (no input to focus).
    const target = firstInputRef.current;
    if (target) {
      target.focus();
      target.setSelectionRange(target.value.length, target.value.length);
    }
  }, []);

  // Global Escape dismissal — the Wrapper-level keydown only fires when
  // focus is inside the popover, but the user expects Escape to close
  // the popover from anywhere (e.g., after clicking back to the canvas
  // or elsewhere outside the popover). Registered with `capture: true`
  // and `stopImmediatePropagation` so the modal's own Escape-to-close
  // handler doesn't ALSO fire and close the entire clip preview when
  // the user only wanted to dismiss the overlay editor.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      // If a confirm is pending, cancel the confirm rather than closing
      // the whole popover — Escape on confirm reverts to idle.
      if (confirmStep) {
        setConfirmStep(null);
        return;
      }
      onClose();
    };
    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, [onClose, confirmStep]);

  // PLAT-10644 — close on mousedown outside the popover, on EVERY host
  // (transcript chips, canvas layer, side panel). A plain document-level
  // hit test, deliberately NOT `useOutsideAlerter` — see the note above:
  // the alerter also treats parent-window blur (any Creatomate iframe
  // click) as an outside click. Genuine iframe clicks never emit a
  // parent-document mousedown at all, so canvas interaction stays
  // governed by the renderer's selection events.
  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      if (e.target instanceof Node && wrapper.contains(e.target)) return;
      // PLAT-11105 — the anchor is the affordance that OPENED this popover (the
      // action pill holding the pencil, a timeline marker chip), so a mousedown
      // on it is not "clicking away": it's a re-press, and what that means is
      // the host's call. Closing here broke every host's toggle — this listener
      // is capture-phase on `document`, so it ran before the pencil's `click`,
      // which then saw a closed popover and reopened it. Hosts already handle
      // the re-press (`handleEdit` in `CompositionCanvasActionsLayer`,
      // `handleMarkerClick` in `LeftSidePanel`), and their explicit dismissals
      // (the pill's X, Delete) clear the anchor themselves, so nothing relies
      // on this path.
      if (e.target instanceof Node && anchorElement.contains(e.target)) return;
      onClose();
    };
    document.addEventListener('mousedown', handleMouseDown, true);
    return () =>
      document.removeEventListener('mousedown', handleMouseDown, true);
  }, [onClose, anchorElement]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (confirmStep) {
          setConfirmStep(null);
          return;
        }
        onClose();
      }
    },
    [onClose, confirmStep],
  );

  const updateRow = useCallback((index: number, value: string) => {
    setRows(prev =>
      prev.map((row, i) => (i === index ? { ...row, value } : row)),
    );
  }, []);

  /**
   * PLAT-11090 — the option index the user has stepped to but not yet saved,
   * per row. Absent means "whatever the provenance blob says is applied", so an
   * untouched row still opens on the option that produced the text on the
   * canvas.
   *
   * Stepping is a draft like every other field in this popover: it moves the
   * row's textarea only, and the element is written on Save. Keyed by row index
   * to match the popover's other per-row state.
   */
  const [pendingOptions, setPendingOptions] = useState<Record<number, number>>(
    {},
  );

  // Discarded whenever the rows re-seed (same deps as that effect): the index
  // describes the rows being replaced. `fields` changing length or order would
  // land it on the wrong row, and because the AI control is original-language
  // only, an `isTranslated` flip (PLAT-10513 — the transcript language toggle
  // reaches this popover while it stays mounted) takes the stepper off screen
  // while leaving Save holding a write to a property that view doesn't edit.
  useEffect(() => {
    setPendingOptions({});
  }, [fields, isTranslated, activeLanguage, compositionPhrases]);

  /**
   * PLAT-10915 — run a generation for an `aiText` row and reflect the result in
   * that row's input.
   *
   * The service has already written the value to the element through
   * `modifyProperty` (E2), so the canvas is right either way; seeding the input
   * keeps the popover from showing the pre-generation text and then clobbering
   * the new value on Save.
   */
  const handleGenerateAIText = useCallback(
    async (
      index: number,
      baseTextScope: AITextBaseTextScope,
      instructions: string,
    ) => {
      const generate = aiTextContext?.generate;
      const field = rows[index]?.field;
      if (!generate || !field) return;
      const generated = await generate({ field, baseTextScope, instructions });
      if (generated == null) return;
      // PLAT-11090 — a run replaces the option list wholesale and lands on its
      // own preferred option, so an index stepped to against the OLD list is
      // meaningless (and could point past the end of the new one).
      setPendingOptions(prev => {
        if (!(index in prev)) return prev;
        const next = { ...prev };
        delete next[index];
        return next;
      });
      updateRow(index, generated);
    },
    [aiTextContext, rows, updateRow],
  );

  /**
   * PLAT-10916 / PLAT-11090 — step to one of the generated alternatives.
   *
   * Draft only: the option's text goes into the row's textarea and the index is
   * held until Save (which routes it back through `selectOption`, the one path
   * that records the pick in the provenance blob). Nothing reaches the element
   * here, so stepping through five hooks and then hitting Cancel leaves the
   * canvas exactly as it was found.
   */
  const handleSelectAITextOption = useCallback(
    (index: number, optionIndex: number) => {
      const field = rows[index]?.field;
      if (!field) return;
      const provenance = aiTextContext?.provenance?.(field.controlId);
      const selected = provenance?.options[optionIndex];
      if (typeof selected !== 'string' || selected === '') return;
      setPendingOptions(prev => {
        const next = { ...prev };
        // Stepping back to the option already on the element leaves nothing to
        // commit — drop the entry rather than saving a write that is a no-op.
        if (optionIndex === provenance?.selectedOption) delete next[index];
        else next[index] = optionIndex;
        return next;
      });
      updateRow(index, selected);
    },
    [aiTextContext, rows, updateRow],
  );

  const updateTranslatedRow = useCallback(
    (index: number, translatedValue: string) => {
      setRows(prev =>
        prev.map((row, i) => (i === index ? { ...row, translatedValue } : row)),
      );
    },
    [],
  );

  // PLAT-10046 — Revert preview. Populates the row's translated input
  // with the AI baseline (falling back to source text on legacy entries
  // without a snapshot), marks the row as `revertPending`, and closes
  // the pin-detail card. NOTHING is committed until the user clicks
  // Save — Cancel discards. If the user edits the input AFTER previewing
  // a revert, Save will dispatch an upsert with the new value (treated
  // as a fresh override) since `translatedValue` no longer matches
  // `revertBaseline`.
  const previewRevert = useCallback((index: number) => {
    setRows(prev =>
      prev.map((row, i) => {
        if (i !== index) return row;
        // Legacy pre-PLAT-10046 pins were stored without
        // `originalTranslatedValue`; for those we fall back to the
        // source-language text so Revert seeds *something* the user
        // can recognize. Post-PLAT-10046 pins always carry a captured
        // baseline (see `upsertCompositionPhraseTranslation` snapshot
        // priority in the store).
        const baseline =
          row.pin.originalTranslatedValue ?? row.field.originalValue;
        return {
          ...row,
          translatedValue: baseline,
          revertPending: true,
          revertBaseline: baseline,
        };
      }),
    );
    setPinDetailIdx(null);
  }, []);

  const toggleOriginal = useCallback((index: number) => {
    setExpandedOriginals(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }, []);

  const handleSave = useCallback(() => {
    // Overlay timing first — invalid input aborts the whole Save (and keeps
    // the popover open) so a bad timecode never silently drops text edits.
    let timingCommitted = 0;
    if (showTiming && timing && onCommitTiming) {
      const start = parseTimecode(startInput, framerate);
      const end = parseTimecode(endInput, framerate);
      if (start === null || end === null) {
        setErrorMessage('Enter a valid start and end time (m:ss:ff).');
        return;
      }
      if (end <= start) {
        setErrorMessage('End time must be after start time.');
        return;
      }
      // Only commit when the user actually changed the timing — the rounded
      // display would otherwise snap a precise value on every Save.
      if (timingDirty) {
        onCommitTiming({ time: start, duration: end - start });
        timingCommitted = 1;
      }
    }

    // Commits the original-language input for one row. Returns the
    // number of writes that landed (0 or 1). Active only when the
    // original textarea is the user's primary input — i.e. we're not
    // in translated mode, OR the row isn't translatable (quick-edit
    // single-input shape from PLAT-9968).
    const commitOriginalLanguageEdit = (row: RowState): number => {
      const originalInputActive = !isTranslated || !row.field.isTranslatable;
      if (!originalInputActive) return 0;
      const dirty =
        row.value !== row.field.originalValue && row.value.trim() !== '';
      if (!dirty) return 0;
      const ok = setCompositionFieldValue({
        editorStore,
        targetElementId: targetId,
        elementPath: row.field.elementPath,
        property: row.field.property,
        newValue: row.value,
      });
      if (ok) return 1;
      // The writer already logs the underlying cause; this surface log
      // makes the silent-drop visible to anyone debugging why Save did
      // nothing.
      logger.warn('Composition quick-edit: original-language write rejected', {
        targetId,
        property: row.field.property,
        elementPath: row.field.elementPath,
      });
      return 0;
    };

    // Commits the translated-input branch for one row. Three outcomes:
    //   - Revert path: user clicked Revert (revertPending) OR typed
    //     the AI baseline manually. Routes to
    //     `removeCompositionPhrasePin` and clears the pin.
    //   - Upsert path: user typed something else that differs from the
    //     resolved seed. Routes to `upsertCompositionPhraseTranslation`.
    //   - No-op when the row isn't translatable, the user is on the
    //     original language, or nothing changed.
    const commitTranslatedRow = (row: RowState): number => {
      if (!isTranslated || !row.field.isTranslatable) return 0;
      if (isRevertOfPin(row)) {
        if (!removeCompositionPhrasePin) {
          // The surface mounted the popover without wiring the revert
          // dispatch — the revert intent is dropped. Flag it so we
          // don't ship a surface that ignores Revert silently.
          logger.warn(
            'Composition quick-edit: revert intent dropped (no removeCompositionPhrasePin dispatcher)',
            { targetId },
          );
          return 0;
        }
        removeCompositionPhrasePin({
          sourceText: row.field.originalValue,
        });
        return 1;
      }
      const upsertDirty =
        row.translatedValue !== row.resolvedValue &&
        row.translatedValue.trim() !== '';
      if (!upsertDirty || !upsertCompositionPhraseTranslation) return 0;
      // PLAT-10046 — when overriding a preset row (no phrase entry yet),
      // pass the preset value as the previous baseline so the store
      // snapshots it into `originalTranslatedValue`. For a `'phrase'`
      // row the cached entry already carries its own baseline; skip
      // the explicit hand-off.
      const previousTranslatedValue =
        row.resolvedSource === 'preset' ? row.resolvedValue : undefined;
      upsertCompositionPhraseTranslation({
        sourceText: row.field.originalValue,
        translatedValue: row.translatedValue,
        ...(previousTranslatedValue !== undefined
          ? { previousTranslatedValue }
          : {}),
      });
      if (compositionType && surface) {
        trackCompositionPhraseEdited({
          story_id: storyId ?? undefined,
          composition_element_id: targetId,
          composition_type: compositionType,
          surface,
          language: activeLanguage,
          source_text_length: row.field.originalValue.length,
        });
      }
      return 1;
    };

    // PLAT-11090 — applies an option the user stepped to while the popover was
    // open. Routed through `selectOption` rather than left to the text commit
    // below because that path ALSO records the pick in the provenance blob, so
    // reopening finds the stepper where the creator left it. Returns the text
    // written, or `null` when there was nothing pending / the write missed.
    const commitPendingOption = (
      row: RowState,
      index: number,
    ): string | null => {
      const optionIndex = pendingOptions[index];
      if (optionIndex === undefined) return null;
      const selectOption = aiTextContext?.selectOption;
      if (!selectOption) return null;
      return selectOption({ field: row.field, optionIndex });
    };

    let originalCommitted = 0;
    let translatedCommitted = 0;
    rows.forEach((row, index) => {
      const appliedOption = commitPendingOption(row, index);
      if (appliedOption !== null) originalCommitted += 1;
      // A draft that is exactly the option just applied needs no second write —
      // it would land the same string again and add an identical undo entry.
      if (appliedOption === null || row.value !== appliedOption) {
        originalCommitted += commitOriginalLanguageEdit(row);
      }
      translatedCommitted += commitTranslatedRow(row);
    });

    if (
      originalCommitted === 0 &&
      translatedCommitted === 0 &&
      timingCommitted === 0
    ) {
      // Nothing changed — just close.
      onClose();
      return;
    }

    // The store-level `upsertCompositionPhraseTranslation` /
    // `removeCompositionPhrasePin` / `commandUpdateElement` paths each
    // log their own info-level summary; this popover-level summary is
    // diagnostic detail (which rows changed, how many), so keep it at
    // debug to avoid log spam on every Save click.
    logger.debug('Composition quick-edit popover saved', {
      targetId,
      originalCommitted,
      translatedCommitted,
      timingCommitted,
    });
    onSaved?.({
      originalRowsCommitted: originalCommitted,
      translatedRowsCommitted: translatedCommitted,
    });
    onClose();
  }, [
    rows,
    isTranslated,
    editorStore,
    targetId,
    pendingOptions,
    aiTextContext,
    upsertCompositionPhraseTranslation,
    removeCompositionPhrasePin,
    compositionType,
    surface,
    storyId,
    activeLanguage,
    showTiming,
    timing,
    onCommitTiming,
    startInput,
    endInput,
    timingDirty,
    framerate,
    onSaved,
    onClose,
  ]);

  // PLAT-10046 — Retranslate click. Single button under translated mode;
  // always dispatches with `force: true` so it replaces any AI-generated
  // translation + pinned manual edits matching the composition's
  // sourceTexts. Computes the shared-phrase footprint to decide whether
  // to open the inline confirm step (more than one composition affected)
  // or dispatch immediately.
  const handleRetranslate = useCallback(() => {
    if (!showRetranslate || !retranslateCompositionPhrases) return;
    if (inFlight) return;
    const translatable = fields.filter(f => f.isTranslatable);
    const affectedRefKeys = new Set<string>();
    for (const f of translatable) {
      const entry = compositionPhrases.get(normalizePhrase(f.originalValue));
      for (const ref of entry?.references ?? []) {
        affectedRefKeys.add(
          `${ref.compositionElementId}|${ref.sourceElement.id}|${ref.property}`,
        );
      }
    }
    const otherCompositionElementIds = new Set<string>();
    for (const key of affectedRefKeys) {
      const compositionElementId = key.split('|', 1)[0];
      if (compositionElementId && compositionElementId !== targetId) {
        otherCompositionElementIds.add(compositionElementId);
      }
    }
    const otherCompositionsCount = otherCompositionElementIds.size;
    const affectedRefsCount = affectedRefKeys.size;

    // Show confirm step only when more than one composition is affected.
    // First-time retranslates against an uncovered composition have an
    // empty footprint (the cache has no entries yet), so they skip the
    // confirm and dispatch immediately.
    // PLAT-10190 — the first-time Translate (force: false) never clobbers
    // existing phrases/pins, so it skips the shared-phrase confirm and
    // dispatches immediately. Retranslate (force: true) keeps the confirm
    // when other compositions share the affected phrases.
    if (otherCompositionsCount > 0 && !isFirstTimeTranslate) {
      setConfirmStep({
        otherCompositionsCount,
        affectedRefsCount,
      });
      return;
    }
    dispatchRetranslate({
      referencesAffected: affectedRefsCount,
      otherCompositionsAffected: otherCompositionsCount,
      force: !isFirstTimeTranslate,
    });
  }, [
    showRetranslate,
    retranslateCompositionPhrases,
    fields,
    compositionPhrases,
    targetId,
    inFlight,
    isFirstTimeTranslate,
  ]);

  const dispatchRetranslate = useCallback(
    async (args: {
      referencesAffected: number;
      otherCompositionsAffected: number;
      force: boolean;
    }) => {
      if (!retranslateCompositionPhrases) return;
      const { referencesAffected, otherCompositionsAffected, force } = args;
      const sourceTexts = fields
        .filter(f => f.isTranslatable)
        .map(f => f.originalValue);
      setInFlight(true);
      setErrorMessage(null);
      setConfirmStep(null);
      // Fire BEHAVIORAL "clicked" before the await so the click intent
      // lands even if the user closes the popover mid-call. Single
      // button → always `composition_retranslate_clicked`.
      const analyticsCtx =
        compositionType && surface
          ? {
              story_id: storyId ?? undefined,
              composition_element_id: targetId,
              composition_type: compositionType,
              surface,
              language: activeLanguage,
              source_texts_count: sourceTexts.length,
              references_affected: referencesAffected,
              other_compositions_affected: otherCompositionsAffected,
            }
          : null;
      if (analyticsCtx) {
        if (force) trackCompositionRetranslateClicked(analyticsCtx);
        else trackCompositionTranslateClicked(analyticsCtx);
      }
      try {
        await retranslateCompositionPhrases({
          // PLAT-10190 — force: false for the first-time Translate (fills the
          // missing rows without clobbering existing phrases/pins); force:
          // true for Retranslate (discards AI translations + non-preset pins
          // and re-runs).
          force,
          compositionElementId: targetId,
          sourceTexts,
        });
        // Success — the cache swap upstream triggers the parent's memo
        // for `compositionPhrases` to invalidate, which re-renders this
        // popover with the new translations resolved (via the
        // `compositionPhrases`-dep `useEffect` that re-seeds rows).
      } catch (error) {
        const info = extractErrorInfo(error);
        const message =
          info.errorMessage ||
          "Couldn't translate this composition. Try again.";
        setErrorMessage(message);
        logger.warn('Composition retranslate failed', {
          targetId,
          ...info,
        });
        if (compositionType && surface) {
          trackCompositionTranslateFailed({
            story_id: storyId ?? undefined,
            composition_element_id: targetId,
            composition_type: compositionType,
            surface,
            language: activeLanguage,
            action: force ? 'retranslate' : 'translate',
            error_message: message,
          });
        }
      } finally {
        setInFlight(false);
      }
    },
    [
      retranslateCompositionPhrases,
      fields,
      targetId,
      storyId,
      compositionType,
      surface,
      activeLanguage,
    ],
  );

  // Split the composition header into a type eyebrow + name so the popover
  // reads "Graphic Overlay" with the element name on its own line beneath.
  // `formatHeaderLabelFromMarker` joins them with " — " (and any active
  // language suffix rides along on the name line). No separator = type only.
  const headerSeparatorIdx = headerLabel.indexOf(' — ');
  const headerTypeLabel =
    headerSeparatorIdx === -1
      ? headerLabel
      : headerLabel.slice(0, headerSeparatorIdx);
  const headerName =
    headerSeparatorIdx === -1
      ? null
      : headerLabel.slice(headerSeparatorIdx + 3);

  return (
    <Wrapper
      ref={wrapperRef}
      placement={placement}
      data-testid="composition-quick-edit-popover"
      onMouseDown={e => e.stopPropagation()}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-label={`Edit ${headerLabel}`}
    >
      <Header>
        {/*
            Title-first header (PLAT-10644): the element name leads with the
            close button pinned flush right; everything secondary collapses
            into ONE muted meta line below — the composition type and the
            "source → target" languages, dot-separated. Languages are stated
            once here so the per-field disclosures below stay a bare
            "Original". Two rows instead of the old three (eyebrow / name /
            language pill).
          */}
        <HeaderTopRow>
          <HeaderName>{headerName ?? headerTypeLabel}</HeaderName>
          {/*
              Design-system close button: 12×12 CloseIcon, no tooltip —
              the icon is self-explanatory (PLAT-10644).
            */}
          <HeaderClose type="button" onClick={onClose} aria-label="Close">
            <CloseIcon width={12} height={12} />
          </HeaderClose>
        </HeaderTopRow>
        {/*
            Two dedicated meta lines (PLAT-10644): the composition type
            (quietest) and the "source → target" languages on its own line
            with a slightly brighter shade — a deliberate hierarchy step
            between them, no "·" separator to orphan on wrap.
          */}
        {headerName ? <HeaderMetaType>{headerTypeLabel}</HeaderMetaType> : null}
        {activeLanguageLabel ? (
          <HeaderMetaLanguages>
            {sourceLanguage
              ? `${formatLanguageCaption(sourceLanguage)} → ${activeLanguageLabel}`
              : activeLanguageLabel}
          </HeaderMetaLanguages>
        ) : null}
      </Header>

      {errorMessage && (
        <ErrorPill role="alert" data-testid="composition-quick-edit-error">
          {errorMessage}
        </ErrorPill>
      )}

      {rows.length === 0 && !showTiming ? (
        <EmptyState>No editable text fields on this composition.</EmptyState>
      ) : rows.length === 0 ? null : (
        <RowList>
          {rows.map((row, idx) => {
            const rowLabel =
              row.field.controlLabel || row.field.sourceElementName;
            const translatableInTranslatedView =
              isTranslated &&
              row.field.isTranslatable &&
              row.resolvedSource !== null;

            if (translatableInTranslatedView) {
              // PLAT-10046 translated mode for translatable rows:
              //   • Editable translated textarea is the primary input.
              //   • Resolution badge sits next to the row label.
              //   • Icon-only chevron disclosure on the right (the
              //     source language is stated once in the header meta —
              //     PLAT-10644); click toggles, hover shows the original
              //     text in a native title tooltip.
              //   • Original textarea (read-only) reveals below when
              //     expanded — visually mirrors a folder tree node.
              const isExpanded = expandedOriginals.has(idx);
              const isFirst = idx === 0;
              const isPinDetailOpen = pinDetailIdx === idx;
              return (
                <FieldRow key={row.field.controlId || idx}>
                  <TranslatedHeaderRow>
                    <TranslatedHeaderLeft>
                      <FieldLabel as="span">{rowLabel}</FieldLabel>
                      {/*
                          PLAT-10046 — badge only shows when this entry is
                          a manual phrase pin. The default "Phrase" / "Preset"
                          / "Missing" labels are noise for the common case
                          (translation just works); the override badge is
                          actionable — click it to inspect the pin metadata
                          + revert.
                        */}
                      {row.pin.pinned && !row.revertPending && (
                        <>
                          {/*
                              PLAT-10046 — Override pill. Layout:
                                [ Override  i  ▸ ]
                              where the caret flips to ▾ when the
                              pin-detail card is open (matches the
                              "Original ▸ en-us" disclosure pattern).
                              Clicking anywhere on the pill toggles the
                              detail card. The tooltip is bound ONLY to
                              the `i` mark so the user sees the
                              propagation note when they hover that
                              specific affordance, not the whole pill.
                            */}
                          <PinBadge
                            type="button"
                            aria-expanded={isPinDetailOpen}
                            aria-label={`Override — ${row.pin.pinnedBy ?? 'unknown editor'}`}
                            onClick={() =>
                              setPinDetailIdx(prev =>
                                prev === idx ? null : idx,
                              )
                            }
                            data-testid={`composition-quick-edit-pin-badge-${idx}`}
                          >
                            Override
                            <PinBadgeInfoMark
                              data-tooltip-id={`composition-quick-edit-pin-info-${idx}`}
                              data-tooltip-content="This will override the same phrase in all compositions across this story."
                              data-testid={`composition-quick-edit-pin-info-${idx}`}
                              aria-label="Override scope: applies to all matching phrases in this story"
                            >
                              i
                            </PinBadgeInfoMark>
                            <DisclosureChevron
                              expanded={isPinDetailOpen}
                              size={8}
                            />
                          </PinBadge>
                          <Tooltip
                            id={`composition-quick-edit-pin-info-${idx}`}
                            style={MODAL_TOOLTIP_STYLE}
                          />
                        </>
                      )}
                    </TranslatedHeaderLeft>
                    {/*
                        Icon-only disclosure (PLAT-10644): the revealed
                        left-rule reference already reads as "the original
                        text", so a text label here was redundant next to
                        the field label. The chevron carries the
                        collapse/expand affordance; the native title
                        tooltip previews the original while collapsed and
                        the aria-label names the control for AT.
                      */}
                    <OriginalDisclosure
                      type="button"
                      onClick={() => toggleOriginal(idx)}
                      aria-expanded={isExpanded}
                      aria-controls={`composition-quick-edit-original-${idx}`}
                      aria-label={`${isExpanded ? 'Hide' : 'Show'} original text`}
                      title={row.field.originalValue}
                      data-testid={`composition-quick-edit-original-toggle-${idx}`}
                    >
                      <DisclosureChevron expanded={isExpanded} size={11} />
                    </OriginalDisclosure>
                  </TranslatedHeaderRow>
                  {row.pin.pinned && isPinDetailOpen && (
                    <PinDetailCard
                      data-testid={`composition-quick-edit-pin-detail-${idx}`}
                      role="region"
                      aria-label="Pin detail"
                    >
                      {/*
                          "Previous translation" surfaces the
                          PRE-OVERRIDE translated value (the AI / preset
                          / earlier-pin baseline) so the user sees what
                          they overrode in the same language they're
                          editing. Revert sits flush-right in the same
                          header row — closest to the value being
                          reverted so the affordance reads as a per-value
                          action, not a card-level action. Legacy entries
                          without `originalTranslatedValue` fall back to
                          the source-language text so the row is always
                          populated.
                        */}
                      <PinDetailRow>
                        <PinDetailHeader>
                          <PinDetailKey>Previous translation</PinDetailKey>
                          {removeCompositionPhrasePin && (
                            <PinDetailRevert
                              type="button"
                              onClick={() => previewRevert(idx)}
                              data-testid={`composition-quick-edit-pin-revert-${idx}`}
                            >
                              Revert
                            </PinDetailRevert>
                          )}
                        </PinDetailHeader>
                        <PinDetailValue
                          data-testid={`composition-quick-edit-pin-detail-source-${idx}`}
                        >
                          {/*
                              Legacy pre-PLAT-10046 pins (and any pin
                              whose first write happened before the
                              snapshot logic landed) have no captured
                              baseline; fall back to the source-language
                              text so the card never renders blank.
                              Post-PLAT-10046 pins always carry a
                              baseline — see the snapshot priority in
                              `upsertCompositionPhraseTranslation`.
                            */}
                          {row.pin.originalTranslatedValue ??
                            row.field.originalValue}
                        </PinDetailValue>
                      </PinDetailRow>
                      <PinDetailRow>
                        <PinDetailKey>Edited by</PinDetailKey>
                        <PinDetailValue>
                          {row.pin.pinnedBy ?? 'Unknown editor'}
                          {row.pin.pinnedAt
                            ? ` on ${formatPinnedAt(row.pin.pinnedAt)}`
                            : ''}
                        </PinDetailValue>
                      </PinDetailRow>
                    </PinDetailCard>
                  )}
                  {/*
                      Original sits ABOVE the editable translated input
                      when expanded. Rendered as an auto-height muted
                      text block (not a textarea) with a darker-gray
                      background. Always mounted so the grid-rows
                      0fr → 1fr transition can animate height smoothly
                      on toggle; `aria-hidden` mirrors the disclosure
                      state for assistive tech.
                    */}
                  <OriginalRevealer
                    data-expanded={isExpanded || undefined}
                    aria-hidden={!isExpanded}
                    data-testid={`composition-quick-edit-original-revealer-${idx}`}
                  >
                    <OriginalReference
                      id={`composition-quick-edit-original-${idx}`}
                      aria-label={`${rowLabel} (original)`}
                      data-testid={`composition-quick-edit-original-${idx}`}
                    >
                      {row.field.originalValue}
                    </OriginalReference>
                  </OriginalRevealer>
                  <AutoGrowTextarea
                    ref={isFirst ? firstInputRef : undefined}
                    data-shimmer={inFlight ? 'true' : undefined}
                    value={row.translatedValue}
                    onChange={e => updateTranslatedRow(idx, e.target.value)}
                    placeholder={
                      row.resolvedSource === 'missing'
                        ? 'Not yet translated'
                        : undefined
                    }
                    aria-label={`${rowLabel} (translated)`}
                    data-testid={`composition-quick-edit-translated-${idx}`}
                  />
                </FieldRow>
              );
            }

            // Original-language row (PLAT-9968 path) OR a
            // non-translatable quick-edit row under a translated
            // language. Either way: a single editable original-language
            // textarea, exactly as PLAT-9968 shipped.
            const isFirst = idx === 0;
            // PLAT-10506 — under a non-original caption language, a field that
            // explicitly opted out of translation shows its original text with
            // a caption explaining why no translation is offered (no Translate
            // / Retranslate — `showRetranslate` already requires a translatable
            // row). Original-language editing is unaffected.
            const showNotTranslatable =
              isTranslated && !row.field.isTranslatable;
            // PLAT-10913 — the AI text control rides BELOW the row's normal
            // textarea, so the generated value stays an ordinary hand-editable
            // text property (E2) and the control is purely additive. Only
            // under the original language: generation writes the
            // original-language property, and offering it from the translated
            // view — where the translation is the primary input — would
            // silently rewrite a field that view doesn't edit.
            const aiText =
              !isTranslated && row.field.controlType === 'aiText'
                ? row.field.aiText
                : undefined;
            // PLAT-10916 — read live on every render so the picker appears the
            // moment a generation lands, without re-extracting `fields`.
            const aiProvenance = aiText
              ? aiTextContext?.provenance?.(row.field.controlId)
              : undefined;
            return (
              <FieldRow key={row.field.controlId || idx}>
                <FieldLabel>{rowLabel}</FieldLabel>
                <AutoGrowTextarea
                  ref={isFirst ? firstInputRef : undefined}
                  value={row.value}
                  onChange={e => updateRow(idx, e.target.value)}
                  aria-label={rowLabel}
                  data-testid={`composition-quick-edit-original-${idx}`}
                />
                {aiText && aiTextContext && (
                  <AITextControl
                    idSuffix={idx}
                    label={rowLabel}
                    // PLAT-10917 DS2 — the LAST RUN's scope wins over the
                    // control's configured default. The blob already records
                    // which scope produced the text on the canvas; reopening
                    // on the DatoCMS default instead would tell the creator
                    // the graphic was built from a selection when it was
                    // built from the whole clip, and the next Regenerate
                    // would quietly change scope on them.
                    defaultBaseTextScope={
                      aiProvenance?.baseTextScope ?? aiText.defaultBaseTextScope
                    }
                    overlay={aiTextContext.overlay}
                    transcriptElements={aiTextContext.transcriptElements}
                    // PLAT-10915 — a real run when the surface wires one; the
                    // PLAT-10913 no-op otherwise.
                    onGenerate={
                      aiTextContext.generate
                        ? request =>
                            void handleGenerateAIText(
                              idx,
                              request.baseTextScope,
                              request.instructions,
                            )
                        : noopGenerate
                    }
                    {...(aiTextContext.status?.(row.field.controlId) ??
                      IDLE_AI_TEXT_STATUS)}
                    // PLAT-10916 — the picker; hidden until a generation has
                    // left two or more options behind (DS5).
                    options={aiProvenance?.options}
                    // PLAT-11090 — the stepper follows the DRAFT: an unsaved
                    // step moves "N of M" even though the element still holds
                    // the previously applied option.
                    selectedOption={
                      pendingOptions[idx] ?? aiProvenance?.selectedOption
                    }
                    // PLAT-10917 DS2 — reopening the popover shows the nudge
                    // that produced the text now on the canvas.
                    defaultInstructions={aiProvenance?.instructions}
                    // PLAT-10918 — the two halves of the staleness compare,
                    // passed straight through. The scope is the one the RUN
                    // read (DS2), which is why it goes separately from
                    // `defaultBaseTextScope` above: that one seeds a selector
                    // the creator can flip, and the compare must not follow it.
                    sourceFingerprint={aiProvenance?.sourceFingerprint}
                    generatedBaseTextScope={aiProvenance?.baseTextScope}
                    onSelectOption={
                      aiTextContext.selectOption
                        ? optionIndex =>
                            handleSelectAITextOption(idx, optionIndex)
                        : undefined
                    }
                    // PLAT-10923 — passed through, not assembled here.
                    analytics={aiTextContext.analytics?.(row.field.controlId)}
                  />
                )}
                {showNotTranslatable && (
                  <NotTranslatableCaption
                    data-testid={`composition-quick-edit-not-translatable-${idx}`}
                  >
                    Field not translatable
                  </NotTranslatableCaption>
                )}
              </FieldRow>
            );
          })}
        </RowList>
      )}

      {showTiming && (
        <TimingSection data-testid="composition-quick-edit-timing">
          <DurationToggle
            type="button"
            aria-expanded={timingExpanded}
            onClick={() => setTimingExpanded(v => !v)}
            data-testid="composition-quick-edit-duration-toggle"
          >
            <DisclosureChevron expanded={timingExpanded} />
            <TimingLabel>Duration</TimingLabel>
            {/*
                PLAT-10992 — the length reads on the collapsed row too, a
                right-aligned summary mirroring the "Advanced" row's, so the
                creator sees it without expanding (expanding reveals the
                editable Start / End below).
              */}
            <DurationValue data-testid="composition-quick-edit-duration">
              {durationDisplay}
            </DurationValue>
          </DurationToggle>
          {timingExpanded && (
            <TimingRow>
              <TimingField>
                <TimingFieldLabel>Start</TimingFieldLabel>
                <TimingInputGroup>
                  <TimingInput
                    value={startInput}
                    onChange={e => {
                      setStartInput(e.target.value);
                      setTimingDirty(true);
                    }}
                    inputMode="numeric"
                    placeholder="0:00:00"
                    aria-label="Overlay start time"
                    data-testid="composition-quick-edit-start"
                  />
                  <Steppers>
                    <StepperButton
                      type="button"
                      tabIndex={-1}
                      aria-label="Increase start time by one frame"
                      onClick={() => stepTiming('start', 1)}
                    >
                      <StepperGlyph data-up aria-hidden="true">
                        <ChevronDownIcon width="8" height="8" />
                      </StepperGlyph>
                    </StepperButton>
                    <StepperButton
                      type="button"
                      tabIndex={-1}
                      aria-label="Decrease start time by one frame"
                      onClick={() => stepTiming('start', -1)}
                    >
                      <StepperGlyph aria-hidden="true">
                        <ChevronDownIcon width="8" height="8" />
                      </StepperGlyph>
                    </StepperButton>
                  </Steppers>
                </TimingInputGroup>
              </TimingField>
              <TimingField>
                <TimingFieldLabel>End</TimingFieldLabel>
                <TimingInputGroup>
                  <TimingInput
                    value={endInput}
                    onChange={e => {
                      setEndInput(e.target.value);
                      setTimingDirty(true);
                    }}
                    inputMode="numeric"
                    placeholder="0:00:00"
                    aria-label="Overlay end time"
                    data-testid="composition-quick-edit-end"
                  />
                  <Steppers>
                    <StepperButton
                      type="button"
                      tabIndex={-1}
                      aria-label="Increase end time by one frame"
                      onClick={() => stepTiming('end', 1)}
                    >
                      <StepperGlyph data-up aria-hidden="true">
                        <ChevronDownIcon width="8" height="8" />
                      </StepperGlyph>
                    </StepperButton>
                    <StepperButton
                      type="button"
                      tabIndex={-1}
                      aria-label="Decrease end time by one frame"
                      onClick={() => stepTiming('end', -1)}
                    >
                      <StepperGlyph aria-hidden="true">
                        <ChevronDownIcon width="8" height="8" />
                      </StepperGlyph>
                    </StepperButton>
                  </Steppers>
                </TimingInputGroup>
              </TimingField>
            </TimingRow>
          )}
        </TimingSection>
      )}

      {(rows.length > 0 || showTiming) && !confirmStep && (
        <ButtonRow>
          {showRetranslate && (
            <FooterActionButton
              type="button"
              disabled={inFlight}
              onClick={handleRetranslate}
              data-testid="composition-quick-edit-retranslate"
              title={
                isFirstTimeTranslate
                  ? 'Translate this composition into the active language.'
                  : 'Re-run the AI translation for this composition. Pinned manual edits will be cleared.'
              }
            >
              {inFlight ? <SpinnerDot aria-hidden="true" /> : null}
              {isFirstTimeTranslate ? 'Translate' : 'Retranslate'}
            </FooterActionButton>
          )}
          <Spacer />
          <SecondaryButton
            type="button"
            onClick={onClose}
            data-testid="composition-quick-edit-cancel"
          >
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="button"
            onClick={handleSave}
            disabled={inFlight}
            data-testid="composition-quick-edit-save"
          >
            Save
          </PrimaryButton>
        </ButtonRow>
      )}

      {confirmStep && (
        <ConfirmStep
          data-testid="composition-quick-edit-confirm"
          role="alertdialog"
          aria-label="Confirm retranslate"
        >
          <ConfirmText>
            {`Replace translations on ${confirmStep.affectedRefsCount} ${pluralize(
              'phrase',
              confirmStep.affectedRefsCount,
            )} shared with ${confirmStep.otherCompositionsCount} other ${pluralize(
              'composition',
              confirmStep.otherCompositionsCount,
            )} in this story. Pinned manual edits will be cleared. Continue?`}
          </ConfirmText>
          <ButtonRow>
            <Spacer />
            <SecondaryButton
              type="button"
              onClick={() => setConfirmStep(null)}
              data-testid="composition-quick-edit-confirm-cancel"
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              type="button"
              disabled={inFlight}
              onClick={() =>
                dispatchRetranslate({
                  referencesAffected: confirmStep.affectedRefsCount,
                  otherCompositionsAffected: confirmStep.otherCompositionsCount,
                  // Confirm step only ever surfaces on the Retranslate path.
                  force: true,
                })
              }
              data-testid="composition-quick-edit-confirm-go"
            >
              Retranslate
            </PrimaryButton>
          </ButtonRow>
        </ConfirmStep>
      )}
    </Wrapper>
  );
};

/**
 * PLAT-10046 — auto-growing textarea. Starts at one row and expands to
 * fit content as the user types. Uses a layout effect to sync the
 * element's height to its scrollHeight on every value change so the
 * textarea never shows a scrollbar; the popover's Wrapper takes care of
 * scrolling when the combined content exceeds the viewport.
 *
 * Implemented as a small forwardRef wrapper around the styled `Textarea`
 * so the caller can still pass a ref through to focus the first input.
 */
const AutoGrowTextarea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(function AutoGrowTextarea(props, ref) {
  const innerRef = useRef<HTMLTextAreaElement | null>(null);
  const setRef = useCallback(
    (el: HTMLTextAreaElement | null) => {
      innerRef.current = el;
      if (typeof ref === 'function') {
        ref(el);
      } else if (ref) {
        ref.current = el;
      }
    },
    [ref],
  );
  // Resize on every render where the value changed. `useLayoutEffect` so
  // we measure + apply before paint and the user doesn't see a flash
  // at the old height.
  React.useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [props.value]);
  // Disable manual resize: the JS sync to scrollHeight would fight the
  // user's drag on every keystroke anyway, so we just hide the handle.
  return (
    <Textarea
      ref={setRef}
      rows={1}
      {...props}
      style={{ resize: 'none', ...(props.style ?? {}) }}
    />
  );
});

/**
 * PLAT-10046 — true when the translated input on a pinned row should be
 * interpreted as a Revert intent. Covers two flavors:
 *
 *   1. The user typed (or the Revert button seeded) the captured AI/
 *      preset baseline (`pin.originalTranslatedValue`).
 *   2. The user clicked Revert on a legacy pin with no captured baseline;
 *      `previewRevert` falls back to `field.originalValue` and stashes
 *      that in `revertBaseline`. If the input still matches, revert.
 *
 * Exported for unit testability — the rule is small but load-bearing
 * (drives Save's revert vs upsert branching), so isolating it lets
 * tests pin it down without spinning up the whole popover.
 */
export function isRevertOfPin(row: RowState): boolean {
  if (!row.pin.pinned) return false;
  if (
    row.pin.originalTranslatedValue !== undefined &&
    row.translatedValue === row.pin.originalTranslatedValue
  ) {
    return true;
  }
  if (
    row.revertPending &&
    row.revertBaseline !== undefined &&
    row.translatedValue === row.revertBaseline
  ) {
    return true;
  }
  return false;
}

function buildInitialRows(
  fields: CompositionQuickEditField[],
  isTranslated: boolean,
  activeLanguage: string,
  compositionPhrases: ReadonlyMap<string, CompositionPhraseTranslation>,
): RowState[] {
  return fields.map(field => {
    let resolvedSource: ResolvedSource | null = null;
    let resolvedValue = '';
    let pin: RowState['pin'] = { pinned: false };
    if (isTranslated && field.isTranslatable) {
      const resolved = resolveCompositionFieldTextWithSource({
        currentValue: field.originalValue,
        activeLanguage,
        compositionPhrasesByNormalizedSource: compositionPhrases,
        controlPreset: field.controlPreset,
      });
      resolvedSource = resolved.source;
      resolvedValue = resolved.source === 'missing' ? '' : resolved.value;
      // PLAT-10046 — surface pin metadata on the row so the badge +
      // detail card can render directly off the seeded snapshot. The
      // Override badge only shows when this entry is pinned. We look the
      // entry up directly by normalized source here (not via the
      // resolver's `entry` passthrough) so the seed is always read off
      // the cache the same way it gets written — keeps the
      // pin-metadata + originalTranslatedValue surface honest even if
      // the resolver's return shape evolves.
      const entry = compositionPhrases.get(
        normalizePhrase(field.originalValue),
      );
      if (entry?.pinnedOverrideByManualEdit) {
        pin = {
          pinned: true,
          ...(entry.pinnedBy ? { pinnedBy: entry.pinnedBy } : {}),
          ...(entry.pinnedAt ? { pinnedAt: entry.pinnedAt } : {}),
          ...(entry.originalTranslatedValue !== undefined
            ? { originalTranslatedValue: entry.originalTranslatedValue }
            : {}),
        };
      }
    }
    return {
      field,
      value: field.originalValue,
      translatedValue: resolvedValue,
      resolvedSource,
      resolvedValue,
      pin,
      revertPending: false,
    };
  });
}

function pluralize(word: string, n: number): string {
  return n === 1 ? word : `${word}s`;
}

export { formatHeaderLabelFromMarker } from '@src/videoTranscriptionProcessor/translation/compositionLabels';

const Wrapper = styled.div.attrs(
  (props: { placement: PopoverPlacement }) => props,
)`
  position: fixed;
  left: ${p => p.placement.left}px;
  top: ${p => p.placement.top}px;
  /* Keep \`width\` in sync with \`POPOVER_WIDTH\` in computePlacement().
     \`box-sizing: border-box\` makes the rendered outer width exactly
     308px (padding + border counted inside). Without it the box would
     be 308 + 28px padding + 2px border = 338px and the clamp math in
     \`computePlacement\` would let the right edge run off the viewport
     by ~30px when the anchor is near the right side. */
  box-sizing: border-box;
  width: 308px;
  max-width: calc(100vw - 16px);
  /* PLAT-10046 — inline max-height derived from the popover's actual
     top so the box scrolls instead of running off the bottom of the
     viewport. The static \`100vh - 32px\` we used to set on this rule
     ignored where the popover actually opened from, which let the
     pin-detail card push content off-screen when expanding downward.
     Wrapper-level \`overflow-y: auto\` below takes over once contents
     exceed this height. */
  max-height: ${p => p.placement.maxHeight}px;
  overflow-y: auto;
  /* Setting only \`overflow-y\` leaves \`overflow-x: visible\`, which the spec
     computes to \`auto\` — so a 1px-too-wide child surfaces a spurious
     horizontal scrollbar along the bottom. Pin it to \`hidden\` so only the
     intended vertical scroll remains. */
  overflow-x: hidden;
  /* Mounted inside a portal whose root has \`pointer-events: none\`; opt
     back in explicitly so the inputs and buttons receive clicks. */
  pointer-events: auto;
  z-index: ${CLIP_PREVIEW_Z.quickEditPopover};

  background: var(--popover);
  color: var(--popover-foreground);
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
  box-shadow: var(--elevation-toast);
  padding: 12px 14px 14px;

  display: flex;
  flex-direction: column;
  gap: 10px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  /* Hairline under the header anchors it as its own zone, mirroring the
     divider above the Duration section — header / fields / duration+footer
     read as three groups instead of one even-spaced stack (PLAT-10644). */
  padding-bottom: 10px;
  border-bottom: 1px solid var(--border);
`;

const HeaderTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

/* Header meta lines under the title (PLAT-10644). Type is the quietest;
   the language line sits a shade brighter — a deliberate hierarchy step:
   title > languages > type. Both reserve the close button's top-right
   column so they never crowd its hover circle. */
const HeaderMetaType = styled.div`
  font-weight: var(--font-weight-regular);
  font-size: 11px;
  line-height: 1.4;
  color: var(--popover-foreground);
  opacity: 0.5;
  padding-right: 28px;
`;

const HeaderMetaLanguages = styled.div`
  font-weight: var(--font-weight-medium);
  font-size: 11px;
  line-height: 1.4;
  color: var(--popover-foreground);
  opacity: 0.8;
  padding-right: 28px;
`;

const HeaderName = styled.div`
  font-weight: var(--font-weight-semibold, 600);
  font-size: 14px;
  line-height: 1.25;
  color: var(--popover-foreground);
  overflow-wrap: anywhere;
`;

/* Design-system close button (docs/design-system.md § Close Buttons):
   12×12 CloseIcon, muted → foreground on hover, --surface-elevated hover
   surface. Compact 4px padding — --spacing-sm reads oversized inside this
   dense popover. */
const HeaderClose = styled.button`
  margin-left: auto;
  flex-shrink: 0;
  background: transparent;
  border: none;
  padding: var(--spacing-xs);
  border-radius: 6px;
  cursor: pointer;
  color: var(--text-muted);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;

  &:hover {
    color: var(--foreground);
    background-color: var(--surface-elevated);
  }
`;

const RowList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

/* Muted 11px field label (matches the shared SectionLabel scale) — the
   field VALUE is the content; its label is quiet meta, subordinate to the
   header title (PLAT-10644). */
const FieldLabel = styled.div`
  font-weight: var(--font-weight-medium);
  font-size: 11px;
  line-height: 1.2;
  color: var(--popover-foreground);
  opacity: 0.65;
`;

/**
 * PLAT-10506 — caption shown beneath a non-translatable field's original-text
 * row under a non-original caption language, so the user understands why no
 * translation is offered (the popover surfaces the original text only).
 */
const NotTranslatableCaption = styled.div`
  font-weight: var(--font-weight-regular);
  font-size: 11px;
  color: var(--popover-foreground);
  opacity: 0.65;
`;

/**
 * PLAT-10046 — row header that lays out the row label + badge (left) and
 * the "Original ▸ <langcode>" disclosure (right, flush). The disclosure
 * toggles whether the read-only original textarea appears underneath the
 * editable translated textarea.
 */
const TranslatedHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const TranslatedHeaderLeft = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

/* Icon-only collapse/expand chevron for the per-field original reference
   (PLAT-10644). */
const OriginalDisclosure = styled.button`
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--radius-input);
  color: var(--popover-foreground);
  opacity: 0.65;
  padding: 3px 5px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  /* Flush-right inside the parent's space-between layout. */
  margin-left: auto;

  &:hover {
    opacity: 1;
    background: var(--surface-elevated);
    border-color: var(--border);
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 1px;
  }
`;

/**
 * PLAT-10046 — clickable "Phrase Override" badge. Accent color (gold) flags
 * "manually authored" so the user's eye is drawn to it whenever they revisit
 * the popover.
 */
const PinBadge = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: var(--font-weight-medium);
  text-transform: none;
  padding: 1px 6px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--accent) 18%, transparent);
  color: var(--accent);
  border: 1px solid color-mix(in srgb, var(--accent) 35%, transparent);
  cursor: pointer;
  white-space: nowrap;
  line-height: 1.4;

  &:hover {
    background: color-mix(in srgb, var(--accent) 28%, transparent);
  }
  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }
  &[aria-expanded='true'] {
    background: color-mix(in srgb, var(--accent) 32%, transparent);
  }
`;

/**
 * PLAT-10046 — circular "i" mark embedded after the Override label.
 * Carries the story-wide propagation tooltip on hover (the tooltip is
 * bound to THIS span, not the parent button, so the user only sees the
 * note when they hover the info indicator specifically). The mark sits
 * inside the pill button so the parent click still toggles the detail
 * card — span doesn't intercept the click.
 */
const PinBadgeInfoMark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--accent) 45%, transparent);
  color: var(--popover);
  font-size: 9px;
  font-weight: var(--font-weight-medium);
  font-style: italic;
  line-height: 1;
  cursor: help;
`;

const PinDetailCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: color-mix(in srgb, var(--accent) 10%, transparent);
  border: 1px solid color-mix(in srgb, var(--accent) 28%, transparent);
  border-radius: var(--radius-input);
  padding: 8px 10px;
  margin-top: 4px;
`;

const PinDetailRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

/**
 * Row-header layout for the pin-detail card: label on the left, action
 * (Revert) flush right. Used for the "Original phrase" row so Revert
 * sits next to the value it would discard.
 */
const PinDetailHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const PinDetailKey = styled.div`
  font-size: 11px;
  font-weight: var(--font-weight-medium);
  color: var(--popover-foreground);
  opacity: 0.7;
`;

const PinDetailValue = styled.div`
  font-size: 12px;
  color: var(--popover-foreground);
  line-height: 1.4;
  word-break: break-word;
`;

/**
 * Inline Revert button — compact, text-style affordance positioned next
 * to the "Original phrase" label. Visually quiet by default so it
 * doesn't compete with the row label, but highlights on hover so the
 * destructive action stays discoverable.
 */
const PinDetailRevert = styled.button`
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--radius-input);
  color: var(--accent);
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: var(--font-weight-medium);
  padding: 2px 8px;
  cursor: pointer;
  line-height: 1.2;
  white-space: nowrap;

  &:hover {
    background: color-mix(in srgb, var(--accent) 18%, transparent);
    border-color: color-mix(in srgb, var(--accent) 35%, transparent);
  }
  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }
`;

const Textarea = styled.textarea`
  background: var(--background);
  color: var(--foreground);
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
  /* Tighter padding + no min-height: with the AutoGrowTextarea wrapper,
     height is driven by content; a fixed 40px min was making single-line
     inputs ("1000 LB (454 KG)") look chunky. */
  padding: 4px 8px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  line-height: 1.4;
  resize: vertical;
  outline: none;

  ${fieldFocusCss}
`;

/**
 * PLAT-10046 — animated revealer for the original-language reference
 * block. Grid `grid-template-rows: 0fr → 1fr` (with overflow hidden on the
 * child) gives a smooth height transition that fits the actual content
 * height without measuring or hard-coding `max-height`. Padding-y +
 * opacity transition in lockstep so the block fades in / out cleanly.
 */
const OriginalRevealer = styled.div`
  display: grid;
  grid-template-rows: 0fr;
  transition:
    grid-template-rows 180ms ease,
    padding 180ms ease,
    opacity 180ms ease;
  padding-top: 0;
  padding-bottom: 0;
  opacity: 0;

  &[data-expanded] {
    grid-template-rows: 1fr;
    padding-top: 0;
    /* Spacing below the reference lives on the revealer (not the child)
       so it collapses to 0 alongside the grid track. A margin on the
       child would render outside the overflow box and leak 4px below
       the revealer even when collapsed. */
    padding-bottom: 4px;
    opacity: 1;
  }

  & > * {
    min-height: 0;
    overflow: hidden;
  }
`;

const EmptyState = styled.div`
  font-size: 12px;
  color: var(--popover-foreground);
  opacity: 0.7;
  padding: 8px 0;
`;

const TimingSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 10px;
  margin-top: 10px;
  border-top: 1px solid var(--border);
`;

const DurationToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  color: var(--popover-foreground);

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

/* Matches the AI control's "Advanced" row label (a muted FieldLabel) so the two
   disclosure rows read as siblings — Duration was previously full-strength
   white and bolder, which made it jump out from every other label. */
const TimingLabel = styled.span`
  font-size: 11px;
  font-weight: var(--font-weight-medium);
  color: var(--popover-foreground);
  opacity: 0.65;
`;

const DurationValue = styled.span`
  margin-left: auto;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  color: var(--popover-foreground);
  opacity: 0.5;
`;

const TimingRow = styled.div`
  display: flex;
  gap: 10px;
`;

const TimingField = styled.label`
  display: flex;
  flex-direction: column;
  gap: 3px;
  /* Each field takes exactly half the row; min-width:0 lets it shrink inside
     the flex row so the End field never overflows the popover. */
  flex: 1 1 0;
  min-width: 0;
`;

const TimingFieldLabel = styled.span`
  font-size: 11px;
  color: var(--popover-foreground);
  opacity: 0.7;
`;

const TimingInputGroup = styled.div`
  display: flex;
  align-items: stretch;
  gap: 4px;
  min-width: 0;
`;

const TimingInput = styled.input`
  font-size: 13px;
  padding: 6px 8px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--background);
  color: var(--popover-foreground);
  flex: 1 1 0;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;

  ${fieldFocusCss}
`;

const Steppers = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
`;

const StepperButton = styled.button`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  flex: 1 1 0;
  padding: 0;
  line-height: 1;
  color: var(--popover-foreground);
  background: var(--background);
  border: 1px solid var(--border);

  &:first-child {
    border-radius: 6px 6px 0 0;
    border-bottom: none;
  }
  &:last-child {
    border-radius: 0 0 6px 6px;
  }
  &:hover {
    background: var(--muted);
  }
  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 1px;
  }
`;

/* Translate / Retranslate is a contextual utility, not the commit action —
   quiet secondary styling keeps Save the single green button in the footer
   (PLAT-10644). */
const FooterActionButton = styled(SecondaryButton)`
  margin-right: auto;
`;

/* Rotating wrapper for the stepper chevrons: ▼ by default, data-up flips
   it into the increase glyph. */
const StepperGlyph = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &[data-up] {
    transform: rotate(180deg);
  }
`;

const ErrorPill = styled.div`
  background: color-mix(in srgb, var(--destructive) 14%, transparent);
  color: var(--destructive);
  border: 1px solid color-mix(in srgb, var(--destructive) 40%, transparent);
  border-radius: var(--radius-input);
  padding: 6px 10px;
  font-size: 12px;
`;

const ConfirmStep = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: var(--primary-tint-light);
  border: 1px solid color-mix(in srgb, var(--primary) 30%, transparent);
  border-radius: var(--radius-input);
  padding: 8px 10px;
`;

const ConfirmText = styled.div`
  font-size: 12px;
  color: var(--popover-foreground);
  line-height: 1.4;
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const SpinnerDot = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid currentColor;
  border-right-color: transparent;
  animation: ${spin} 0.7s linear infinite;
`;
